// src/pages/admin/AdminDashboard.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { FaHome, FaBox, FaUsers, FaFileAlt, FaHistory, FaFileInvoiceDollar, FaBoxes, FaBullhorn, FaChartLine } from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';
import DashboardHome from './Dashboard';
import axios from 'axios';
import CampaignsReports from './reports/CampaignsReports';
import SchemesReports from './reports/SchemesReports';
import CampaignManagement from './CampaignManagement';
import CRMReports from './reports/CRMReports';
import Items from './reports/Items';

// Icon mapping for admin features
const featureIcons = {
  'campaign-management': FaBullhorn,
  'campaign-reports': FaChartLine,
  'schemes-reports': FaFileAlt,
  'crm-reports': FaUsers,
  'delivery-orders-new': FaFileAlt,
  'items': FaBox,
  'quotation': FaFileInvoiceDollar,
  'activity-log': FaHistory,
  'tally-quotation': FaFileInvoiceDollar,
  'tally-items': FaBoxes,
  'dashboard': FaHome
};

// Add the helper function
const findMatchingDomainConfig = (domains = [], hostname) => {
  if (!domains || !domains.length || !hostname) return null;
  
  // First try exact match
  const exactMatch = domains.find(d => d.domainName === hostname);
  if (exactMatch) return exactMatch;

  // Then try includes match
  return domains.find(d => 
    hostname.includes(d.domainName) || 
    d.domainName.includes(hostname)
  );
};

// Add function to find all matching companies for a domain
const findAllMatchingCompanies = (companies = [], hostname) => {
  if (!companies || !companies.length || !hostname) return [];
  
  return companies.filter(company => {
    if (!company.domains || !Array.isArray(company.domains)) return false;
    return company.domains.some(domain => 
      domain.domainName === hostname || 
      hostname.includes(domain.domainName) || 
      domain.domainName.includes(hostname)
    );
  });
};

const AdminDashboard = () => {
  const { logout, user, currentDomain, activeDomainConfig, setActiveDomainConfig } = useContext(AuthContext);
  const [dashboardAccess, setDashboardAccess] = useState(['dashboard']);
  const [loading, setLoading] = useState(false);
  const [matchingCompany, setMatchingCompany] = useState(null);
  const [companyFetched, setCompanyFetched] = useState(false);
  
  // Update the fetchCompanies function in AdminDashboard.js
  useEffect(() => {
    const fetchCompanies = async () => {
      // Enhanced condition to catch any case where admin lacks full company data
      if (user?.role === 'Admin' && 
          (!user.user.company || 
           typeof user.user.company === 'string' || 
           !user.user.company.domains || 
           user.user.company.domains.length === 0)) {
        
        setLoading(true);
        try {
          // Create a secure API instance with authorization header
          const api = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            timeout: 10000,
            withCredentials: true,
          });
          
          // Add token to headers
          const token = localStorage.getItem('token');
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
          
          // First try to fetch admin's specific company if ID is available
          if (user.user.company && typeof user.user.company === 'string') {
            try {
              const companyResponse = await api.get(`/companies/${user.user.company}`);
              const company = companyResponse.data;
              
              if (company && company.domains) {
                const matchingDomain = findMatchingDomainConfig(company.domains, currentDomain);
                if (matchingDomain) {
                  setMatchingCompany(company);
                  setDashboardAccess(matchingDomain.adminDashboardAccess || ['dashboard']);
                  
                  // Update activeDomainConfig in context
                  if (setActiveDomainConfig) {
                    setActiveDomainConfig(matchingDomain);
                  }
                  setLoading(false);
                  setCompanyFetched(true);
                  return; // Exit early if we found a match
                }
              }
            } catch (err) {
              console.error('Error fetching admin company:', err);
              // Continue to try all companies
            }
          }
            // If specific company fetch failed or had no matching domain, try all companies
          const response = await api.get('/companies');
          const companies = response.data;
          
          // Find all companies with a matching domain
          const matchingCompanies = findAllMatchingCompanies(companies, currentDomain);
          
          if (matchingCompanies.length > 0) {
            // Log if multiple companies are found
            if (matchingCompanies.length > 1) {
              console.log(`Multiple companies found for domain ${currentDomain}:`, 
                matchingCompanies.map(c => ({ id: c._id, name: c.name }))
              );
            }
            
            // Use the first matching company for now
            const company = matchingCompanies[0];
            
            // Get matching domain configuration from company
            const matchingDomain = findMatchingDomainConfig(company.domains, currentDomain);
            if (matchingDomain) {
              setMatchingCompany(company);
              setDashboardAccess(matchingDomain.adminDashboardAccess || ['dashboard']);
              
              // Update activeDomainConfig in context with company info
              if (setActiveDomainConfig) {
                setActiveDomainConfig({
                  ...matchingDomain,
                  companyId: company._id,
                  companyName: company.name
                });
              }
              
              // Store all matching companies in localStorage for campaign management to use
              localStorage.setItem('matchingCompanies', JSON.stringify(
                matchingCompanies.map(c => ({ 
                  _id: c._id, 
                  name: c.name,
                  domains: c.domains
                }))
              ));
            }
          }
        } catch (error) {
          console.error('Error fetching companies:', error);
        } finally {
          setLoading(false);
          setCompanyFetched(true);
        }
      }
    };
    
    fetchCompanies();
  }, [user, currentDomain, setActiveDomainConfig]);
  
  // Use the defined function in useMemo
  const isDomainMatched = useMemo(() => {
    // If we already found a matching company through fetch
    if (matchingCompany) return true;
    
    // Otherwise check if user's company has matching domain
    if (!user?.user?.company?.domains || !currentDomain) return false;
    return findMatchingDomainConfig(user.user.company.domains, currentDomain) !== null;
  }, [user, currentDomain, matchingCompany]);
  
  const getFilteredLinks = () => {
    // Always include dashboard
    const allLinks = [
      { 
        to: "/admin/dashboard", 
        key: "dashboard", 
        icon: FaHome, 
        label: "Dashboard", 
        component: <DashboardHome /> 
      },      { 
        to: "/admin/campaign-management",
        key: "campaign-management",
        icon: FaBullhorn,
        label: "Campaign Management",
        component: <CampaignManagement />
      },
      { 
        to: "/admin/campaign-reports",
        key: "campaign-reports",
        icon: FaChartLine,
        label: "Campaign Reports",
        component: <CampaignsReports />
      },
      { 
        to: "/admin/schemes-reports",
        key: "schemes-reports",
        icon: FaFileAlt,
        label: "Schemes Reports (Legacy)",
        component: <SchemesReports />
      },
      { 
        to: "/admin/crm-reports",
        key: "crm-reports",
        icon: FaUsers,
        label: "CRM Reports",
        component: <CRMReports />
      },
      { 
        to: "/admin/delivery-orders-new",
        key: "delivery-orders-new",
        icon: FaFileAlt,
        label: "Delivery Orders",
        component: <div>Delivery Orders</div>
      },
      { 
        to: "/admin/items",
        key: "items",
        icon: FaBox,
        label: "Items",
        component: <Items/>
      }
   ];

    // Get access array - either from activeDomainConfig or from fetched data
    const accessArray = activeDomainConfig?.adminDashboardAccess || 
                       (matchingCompany ? dashboardAccess : ['dashboard']);

    // Add links based on permitted features
    accessArray.forEach(feature => {
      if (feature === 'dashboard') return;

      // Skip campaign-management if already added to allLinks
      if (feature === 'campaign-management') return;

      // Skip standalone CRM (keep CRM Reports)
      if (feature === 'crm') return;

      allLinks.push({
        to: `/admin/${feature}`,
        key: feature,
        icon: featureIcons[feature] || FaBox,
        label: feature.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '),
        component: <div>{feature.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')}</div>
      });
    });

    return allLinks;
  };
  
  const sidebarLinks = getFilteredLinks();
  const currentPath = window.location.pathname;
  const matchingLink = sidebarLinks.find(link => link.to === currentPath);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout 
        title="Admin Dashboard"
        sidebarLinks={[{ to: "/admin/dashboard", key: "dashboard", icon: FaHome, label: "Dashboard" }]}
        onLogout={() => { logout(); window.location.href = "/"; }}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600 mb-4" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-gray-600">Loading dashboard configuration...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Admin"
      sidebarLinks={sidebarLinks}
      onLogout={() => {
        logout();
        window.location.href = "/";
      }}
    >
      
      {matchingLink ? (
        matchingLink.component
      ) : (
        <div className="p-8 text-center">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h2 className="text-xl font-semibold text-yellow-700 mb-2">Access Restricted</h2>
            <p className="text-yellow-600">
              You don't have access to this section for domain{" "}
              <strong>{currentDomain}</strong>.
            </p>
            <p className="mt-4 text-yellow-600">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminDashboard;